import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import getUserData from "api/getUserData";
import { medical } from "@constants/medical";
import { useTranslation } from "react-i18next";
import {
  getAccessToken,
  setAccessToken,
  clearAuthTokens,
} from "context/OAuth/authService";

// --- 1. Define Separate Contexts ---
export const AuthStatusContext = createContext(); // For token, login/logout
export const UserDataContext = createContext(); // For user object, subscription, flags
export const AuthLoadingContext = createContext(); // For initial loading state

const WORDPRESS_DOMAIN = process.env.REACT_APP_WORDPRESS_DOMAIN;
const LARAVEL_API = process.env.REACT_APP_LARAVEL_API;

// --- Custom Hooks for Consumers ---
export const useAuthStatus = () => useContext(AuthStatusContext);
export const useUserData = () => useContext(UserDataContext);
export const useAuthLoading = () => useContext(AuthLoadingContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => getAccessToken());
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const [medicalView, setMedicalView] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [weeklyCheckIn, setWeeklyCheckIn] = useState(false);
  const [isActionLocked, setIsActionLocked] = useState(false);
  const [showCreatePlan, setShowCreatePlan] = useState(false);
  const [language, setLanguage] = useState("en");

  // Helper to reset all auth context states
  const resetAuthContext = () => {
    setUser(null);
    setToken(null);
    setIsAuthLoading(false);
    setMedicalView(false);
    setSubscription(null);
    setWeeklyCheckIn(false);
    setIsActionLocked(false);
    setShowCreatePlan(false);
    setLanguage("en");
  };

  const { i18n } = useTranslation();

  const handleSetLanguage = useCallback(
    (lang) => {
      i18n.changeLanguage(lang);
      setLanguage(lang);
    },
    [i18n],
  );

  const syncLogoutWithWordPress = async (currentToken) => {
    try {
      await fetch(`${WORDPRESS_DOMAIN}/wp-json/custom-sso/v1/react-logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: currentToken,
          source: "react_dashboard",
          timestamp: new Date().toISOString(),
        }),
        credentials: "include",
      });
    } catch (error) {
      console.error("WordPress logout notification failed:", error);
    }
  };

  async function login({ email, password }) {
    const base = WORDPRESS_DOMAIN;
    if (!base) {
      return {
        ok: false,
        data: { message: "WordPress URL is not configured." },
      };
    }

    const res = await fetch(`${base}/wp-json/app-auth/v1/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ email, password }),
    });

    let data = {};
    try {
      data = await res.json();
    } catch {
      data = { message: "Invalid response from server." };
    }

    return { ok: res.ok && data?.status === true, status: res.status, data };
  }

  const isAuthenticated = !!(token && user);

  const fetchUser = async () => {
    try {
      const userData = await getUserData();

      const subscriptionData = userData?.subscriptions;
      const isMedicalCompleted =
        userData?.is_form_completed === medical.ALLOWED;
      const actualLanguage =
        userData?.language === "English"
          ? "en"
          : userData?.language === "Nederlands"
            ? "nl"
            : "en";

      setUser(userData);
      localStorage?.setItem(
        "is_google_fit_connect",
        userData?.is_google_fit_connect,
      );
      setSubscription(subscriptionData);
      setWeeklyCheckIn(
        userData?.is_manual_edit_allowed &&
          subscriptionData?.status === "active",
      );
      setShowCreatePlan(userData?.create_my_plan);
      setMedicalView(
        isMedicalCompleted && subscriptionData?.status === "active",
      );
      handleSetLanguage(actualLanguage);
    } catch (error) {
      console.error("Failed to fetch user:", error);
    } finally {
      setIsAuthLoading(false);
    }
  };

  const requestPasswordReset = async (email) => {
    const base = WORDPRESS_DOMAIN;
    if (!base) {
      return {
        ok: false,
        data: { message: "WordPress URL is not configured." },
      };
    }
    try {
      const res = await fetch(
        `${base}/wp-json/app-auth/v1/forgot-password/send-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ email }),
        },
      );
      return res;
    } catch (error) {
      console.error("Password reset request failed:", error);
    }
  };

const verifyPasswordResetLink = async (payload) => {
  const base = WORDPRESS_DOMAIN;
  if (!base) {
    return {
      ok: false,
      data: { message: "WordPress URL is not configured." },
    };
  }
  
  try {
    // 1. Payload object ko query string mein convert karein (e.g., ?token=abc&expiry=123)
    const queryString = new URLSearchParams(payload).toString();

    // 2. URL ke sath query string attach karein
    const res = await fetch(
      `${base}/wp-json/app-auth/v1/forgot-password/verify-link?${queryString}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      }
    );

    // Frontend par handle karne ke liye response return karein
    return res;
  } catch (error) {
    console.error("Password reset request failed:", error);
    return { ok: false, data: { message: error.message } };
  }
};

  const requestPasswordConfirm = async (payload) => {
    const base = WORDPRESS_DOMAIN;
    if (!base) {
      return {
        ok: false,
        data: { message: "WordPress URL is not configured." },
      };
    }
    try {
      const res = await fetch(
        `${base}/wp-json/app-auth/v1/forgot-password/reset-link`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(payload),
        },
      );
      return res;
    } catch (error) {
      console.error("Password reset request failed:", error);
    }
  };

  const completeLoginWithToken = async (
    ssoToken,
    { persistent = true } = {},
  ) => {
    setAccessToken(ssoToken, persistent);
    setToken(ssoToken);
  };

  // Only fetch once on mount
  useEffect(() => {
    if (getAccessToken()) {
      fetchUser();
    }
  }, [getAccessToken()]);

  const authStatusValue = {
    token,
    completeLoginWithToken,
    isAuthenticated,
    setToken,
    login,
    requestPasswordReset,
    verifyPasswordResetLink,
    requestPasswordConfirm,
    resetAuthContext,
  };

  const userDataValue = {
    user,
    setUser,
    subscription,
    setSubscription,
    medicalView,
    setMedicalView,
    language,
    handleSetLanguage,
    weeklyCheckIn,
    setWeeklyCheckIn,
    isActionLocked,
    setIsActionLocked,
    showCreatePlan,
    setShowCreatePlan,
  };

  const authLoadingValue = {
    isAuthLoading,
    setIsAuthLoading,
  };

  return (
    <AuthStatusContext.Provider value={authStatusValue}>
      <UserDataContext.Provider value={userDataValue}>
        <AuthLoadingContext.Provider value={authLoadingValue}>
          {children}
        </AuthLoadingContext.Provider>
      </UserDataContext.Provider>
    </AuthStatusContext.Provider>
  );
};
